import { ZERO, compareMoney, isNegative, isPositive, minus, sameMoney, sumMoney, times, type Money } from "../money/exact";
import { DEYN, isPayMethod } from "./wallets";

/**
 * A sale's money: what each line comes to, what the whole sale comes to, and
 * whether the payments cover it exactly.
 *
 * Checked on the phone, so Finish sale is only offered when it will work, and
 * again on the server by this same function, which is the check that counts.
 */

export interface SaleLineInput {
  /** How many of the chosen size. */
  qty: number;
  /** The size's price, as set on the medicine. */
  normalPrice: Money;
  /**
   * What the customer is charged for one of that size.
   *
   * Anybody allowed to sell may lower it or raise it, for this customer only.
   * A raised one is recorded as a discount below zero, so the normal price
   * stays on the line.
   */
  price: Money;
  /**
   * What the line comes to, when that is not price × qty: a shop's sale by
   * money — "$1 of sugar" is 952 g at $0.00105 a gram, and comes to exactly
   * the $1 asked for (DukaanBook Phase 2). Left out everywhere else.
   */
  total?: Money;
}

export interface PaymentInput {
  method: string;
  amount: Money;
}

export type SaleProblem =
  | { code: "no_lines" }
  | { code: "qty_not_positive"; line: number }
  | { code: "price_negative"; line: number }
  | { code: "unknown_wallet"; payment: number }
  | { code: "wallet_twice"; payment: number }
  | { code: "amount_not_positive"; payment: number }
  /** The discount on the whole sale: never below zero, never more than the medicines come to. */
  | { code: "discount_negative" }
  | { code: "discount_too_big" }
  /** Deyn is owed by somebody: their name and phone number are needed to find them. */
  | { code: "credit_needs_customer" }
  /** The payments add up to more or less than the total. */
  | { code: "payments_do_not_match" };

/** Exact, as every figure: 3 tablets at $0.005 come to $0.015, not a cent. */
export interface SaleTotals {
  /** Every line at its normal price. */
  subtotal: Money;
  /**
   * Everything taken off: what changed prices took off (below zero when they
   * added more), and the discount on the whole sale.
   */
  discount: Money;
  /**
   * The discount on the whole sale, as an amount — "$4, less $0.50" — never a
   * percentage (the owner, 2026-10-15). Part of `discount`.
   */
  saleDiscount: Money;
  /** What the lines come to at the prices charged, before the sale's discount. */
  linesTotal: Money;
  /** What the customer pays. */
  total: Money;
}

export function lineTotal(line: SaleLineInput): Money {
  return line.total ?? times(line.price, line.qty);
}

/**
 * How much a changed price took off one line: zero at the normal price, below
 * zero for a raised one. For a sale by money, the few ten-thousandths between
 * the grams at their normal price and the money asked for.
 */
export function lineDiscount(line: SaleLineInput): Money {
  return minus(times(line.normalPrice, line.qty), lineTotal(line));
}

export function saleTotals(lines: readonly SaleLineInput[], saleDiscount: Money = ZERO): SaleTotals {
  const subtotal = sumMoney(lines.map((line) => times(line.normalPrice, line.qty)));
  const linesTotal = sumMoney(lines.map(lineTotal));
  return {
    subtotal,
    discount: sumMoney([...lines.map(lineDiscount), saleDiscount]),
    saleDiscount,
    linesTotal,
    total: minus(linesTotal, saleDiscount),
  };
}

export function paidTotal(payments: readonly PaymentInput[]): Money {
  return sumMoney(payments.map((p) => p.amount));
}

export function checkSale(input: {
  lines: readonly SaleLineInput[];
  payments: readonly PaymentInput[];
  /** Who bought it, as typed. Needed only when some of it is on credit. */
  customer?: { name: string | null; phone: string | null };
  /** Taken off the whole sale, as an amount. */
  saleDiscount?: Money;
}): SaleProblem[] {
  const { lines, payments } = input;
  const saleDiscount = input.saleDiscount ?? ZERO;
  if (lines.length === 0) return [{ code: "no_lines" }];

  const problems: SaleProblem[] = [];

  lines.forEach((line, i) => {
    if (!Number.isInteger(line.qty) || line.qty <= 0) problems.push({ code: "qty_not_positive", line: i });
    if (isNegative(line.price)) problems.push({ code: "price_negative", line: i });
  });

  if (isNegative(saleDiscount)) problems.push({ code: "discount_negative" });
  else if (problems.length === 0 && compareMoney(saleDiscount, saleTotals(lines).linesTotal) > 0) {
    problems.push({ code: "discount_too_big" });
  }

  const seen = new Set<string>();
  payments.forEach((payment, i) => {
    if (!isPayMethod(payment.method)) problems.push({ code: "unknown_wallet", payment: i });
    else if (seen.has(payment.method)) problems.push({ code: "wallet_twice", payment: i });
    seen.add(payment.method);
    if (!isPositive(payment.amount)) problems.push({ code: "amount_not_positive", payment: i });
  });

  if (payments.some((p) => p.method === DEYN) && (!input.customer?.name?.trim() || !input.customer?.phone?.trim())) {
    problems.push({ code: "credit_needs_customer" });
  }

  // Only meaningful once every line has a price to add up. Exactly: no rounding either side.
  if (problems.length === 0 && !sameMoney(paidTotal(payments), saleTotals(lines, saleDiscount).total)) {
    problems.push({ code: "payments_do_not_match" });
  }

  return problems;
}

/**
 * The number printed on a receipt: 42 becomes "0042".
 *
 * Four digits because that is what a pharmacy reaches in its first months, and
 * a short number is one somebody can read out over the phone. It simply grows
 * past 9999 rather than wrapping.
 */
export function formatReceiptNo(n: number): string {
  return String(n).padStart(4, "0");
}
